/**
 * TypeScript Error Type Definitions
 */

export interface AppError {
  message: string
  name?: string
  stack?: string
  statusCode?: number
  statusMessage?: string
  data?: any
  cause?: unknown
}

export interface FetchError extends AppError {
  statusCode: number
  statusMessage: string
  data?: {
    message?: string
    error?: string
    details?: any
  }
}

export interface SupabaseError {
  message: string
  details?: string
  hint?: string
  code?: string
}

export interface AuthError extends AppError {
  statusCode: 401 | 403
  statusMessage: string
}

export function isAppError(error: unknown): error is AppError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as any).message === 'string'
  )
}

export function hasMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as any).message === 'string'
  )
}

export function isFetchError(error: unknown): error is FetchError {
  return (
    isAppError(error) &&
    'statusCode' in error &&
    typeof (error as any).statusCode === 'number'
  )
}

export function isAuthError(error: unknown): error is AuthError {
  return (
    isFetchError(error) &&
    ((error as any).statusCode === 401 || (error as any).statusCode === 403)
  )
}

export function safeErrorMessage(
  error: unknown,
  defaultMessage = 'An unexpected error occurred'
): string {
  if (error == null) return defaultMessage
  if (typeof error === 'string') return error
  if (hasMessage(error)) return error.message
  if (typeof error === 'object' && 'statusMessage' in error) {
    const statusMessage = (error as any).statusMessage
    if (typeof statusMessage === 'string') return statusMessage
  }
  if (
    typeof error === 'object' &&
    'data' in error &&
    typeof (error as any).data === 'object' &&
    (error as any).data !== null &&
    'message' in (error as any).data
  ) {
    const dataMessage = (error as any).data.message
    if (typeof dataMessage === 'string') return dataMessage
  }
  if (typeof error === 'object' && typeof (error as any).toString === 'function') {
    const stringified = (error as any).toString()
    if (stringified !== '[object Object]') return stringified
  }
  return defaultMessage
}

export function safeErrorStatusCode(error: unknown, defaultCode = 500): number {
  if (error == null) return defaultCode
  if (typeof error === 'object') {
    if ('statusCode' in error && typeof (error as any).statusCode === 'number') return (error as any).statusCode
    if ('status' in error && typeof (error as any).status === 'number') return (error as any).status
    if ('code' in error && typeof (error as any).code === 'number') return (error as any).code
  }
  return defaultCode
}

export function createTypedError(message: string, statusCode = 500, data?: any): AppError {
  return { message, statusCode, statusMessage: message, data, name: 'AppError' }
}

export type ErrorRef = Ref<AppError | null>
export type NullableError = AppError | null | undefined
export type ErrorHandler = (error: unknown) => void
export type AsyncErrorHandler = (error: unknown) => Promise<void>
